/**
 * The thread on one request — everything that went out, and everything that
 * came back.
 *
 * Josh, 2026-09-05: "if you send a message to an underwriter from within the
 * portal wouldn't the best way to use it be that they can reply back to the
 * portal and then that way statuses can be immediate." This is the visible half
 * of that. The routing and storage already work; until now there was nowhere to
 * read the result.
 *
 * ⚠️ ONE COMPONENT FOR ALL FOUR REQUEST TYPES, BECAUSE THE THREAD IS THE SAME
 * THING EVERY TIME. Loss runs, reported losses, binders and submissions differ
 * in what they ask for and how they are chased; a chronological list of emails
 * about one reference does not differ at all. The API endpoint is already
 * generic — `/api/requests/:id/messages` decodes the prefix itself — so a
 * per-programme copy of this would be three copies drifting apart.
 *
 * ⚠️ NOTHING HERE RENDERS body_html. The stored HTML is a carrier's own markup
 * arriving from outside the building; putting it in the page would be handing a
 * stranger a script tag on an authenticated origin. `body_text` is what shows.
 * That does mean a reply written entirely inside an HTML table can flatten to
 * something terse — an honest, safe loss, and the attachment is usually the
 * part that mattered.
 */
import { useEffect, useState } from 'react';
import { splitQuoted } from '@commpass/shared';
import { ApiError, api, type RequestMessage } from '../lib/api';

const when = (iso: string): string =>
  new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const size = (bytes: number): string =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/**
 * One message's text: what was written, and a way back to what was carried.
 *
 * ⚠️ INBOUND ONLY. Our own letters have no quoted history to fold, and their
 * signature block would trip the very markers this looks for — the thread would
 * start hiding the bottom third of every letter we sent.
 *
 * ⚠️ THE QUOTED HALF IS ALWAYS REACHABLE, NEVER DISCARDED. A desk that answers
 * inline under each policy number puts the only useful content below the
 * marker, so the toggle is the feature and not a nicety. splitQuoted refuses to
 * fold a message whose reply would be empty, which covers the worst of that.
 *
 * Its own component so the open state belongs to the message rather than to a
 * map of ids held by the list.
 */
function MessageBody({ m }: { m: RequestMessage }): JSX.Element | null {
  const [open, setOpen] = useState(false);

  if (!m.body_text) return null;
  // Plain text only — never body_html. See the file header.
  if (m.direction !== 'in') return <p className="rt-body">{m.body_text}</p>;

  const { reply, quoted } = splitQuoted(m.body_text);

  return (
    <>
      <p className="rt-body">{reply}</p>
      {quoted && (
        <>
          <button
            type="button"
            className="rt-more"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? 'Hide quoted original' : 'Show quoted original'}
          </button>
          {open && <p className="rt-body rt-quoted">{quoted}</p>}
        </>
      )}
    </>
  );
}

export function RequestThread({ reference }: { reference: string }): JSX.Element {
  const [msgs, setMsgs] = useState<RequestMessage[] | null>(null);
  const [err, setErr] = useState('');
  const [opening, setOpening] = useState('');

  useEffect(() => {
    let live = true;
    api
      .requestMessages(reference)
      .then((r) => {
        if (live) setMsgs(r.messages);
      })
      .catch((e: unknown) => {
        if (!live) return;
        setMsgs([]);
        setErr(e instanceof ApiError ? e.message : 'Could not load the thread.');
      });
    return () => {
      live = false;
    };
  }, [reference]);

  /*
    ⚠️ FETCH THE LINK, THEN OPEN IT — the URL is signed and lives 120 seconds,
    so it cannot be an ordinary href rendered with the list. Opening in a new
    tab rather than navigating, because losing the page you were working on to
    download a PDF is its own small insult.
  */
  const openFile = async (m: RequestMessage, index: number) => {
    const key = `${m.id}:${index}`;
    setOpening(key);
    setErr('');
    try {
      const { url } = await api.requestAttachmentUrl(reference, m.id, index);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not open that file.');
    } finally {
      setOpening('');
    }
  };

  if (msgs === null) return <p className="rt-empty">Loading the thread…</p>;

  if (!msgs.length) {
    return (
      <p className="rt-empty">
        {err || 'Nothing recorded on this one yet.'}
        {!err && (
          <span>
            What goes out and what comes back both land here. A request sent before this was
            switched on will stay empty.
          </span>
        )}
      </p>
    );
  }

  return (
    <div className="rt">
      {err && <p className="is-warn rt-err">{err}</p>}
      <ol className="rt-list">
        {msgs.map((m) => (
          <li key={m.id} className={m.direction === 'in' ? 'rt-in' : 'rt-out'}>
            <div className="rt-head">
              <b>
                {m.direction === 'in'
                  ? m.from_name || m.from_email || 'Carrier'
                  : `You → ${m.to_emails.join(', ') || '—'}`}
              </b>
              <span className="rt-when">{when(m.created_at)}</span>
            </div>
            {m.subject && <div className="rt-subject">{m.subject}</div>}
            <MessageBody m={m} />

            {m.attachments.length > 0 && (
              <div className="rt-files">
                {m.attachments.map((f, i) => (
                  <button
                    key={f.storagePath || `${f.name}-${i}`}
                    type="button"
                    className="rt-file"
                    disabled={opening === `${m.id}:${i}`}
                    onClick={() => void openFile(m, i)}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M6 3h9l4 4v14H6zM14 3v5h5" />
                    </svg>
                    <span>{f.name}</span>
                    <em>{opening === `${m.id}:${i}` ? 'opening…' : size(f.size)}</em>
                  </button>
                ))}
              </div>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}
